import type { RatecardConnection } from "~/models/Connection";
import {
    ApiBaseService,
    type EnhancedFetch,
    type EnhancedFetchOptions,
} from "~/services/ApiBaseService";
import type { ApiResponse } from "~/types/ApiResponse";

export interface RatecardSurveyBase<T extends string> {
    type: T;
    label: string;
    description: string;
    value: string;
    status: string;
    subject: string;
    from: string;
    limits: {
        contacts: {
            no_duplicates: boolean;
            no_duplicates_in_any_group: boolean;
        };
        messages: {
            max: number;
            period: "d" | "w" | "m" | "q" | "hy" | "y";
            include_reminders: boolean;
        };
        filter: {
            tag: string | null;
            value: string | null;
        };
    };
    delay: number;
    reminder: {
        enabled: boolean;
        days: number;
        subject: string;
    };
    question_count: number;
    preview_url: string;
    edit_mail_url: string;
    edit_survey_url: string;
}

export type RatecardSurveyTemplate = RatecardSurveyBase<"template">;

export interface RatecardSurvey extends RatecardSurveyBase<"survey"> {
    smart_fields: {
        label: string;
        type: string;
        value: string;
    }[];
}

export interface RatecardAccount {
    label: string;
    value: string;
    avatar: { src: string; alt: string };
    access_token: string;
}

export default class RatecardService extends ApiBaseService {
    public find(
        options?: EnhancedFetchOptions,
    ): EnhancedFetch<RatecardConnection> {
        return this.enhancedFetch<RatecardConnection>(
            this.route(
                "api.connections.ratecard.show",
                "/connections/ratecard",
            ),
            options,
        );
    }

    public connect(
        data: RatecardConnection["token"],
    ): Promise<ApiResponse<RatecardConnection>> {
        return this.request(
            this.route(
                "api.connections.ratecard.store",
                "/connections/ratecard",
            ),
            {
                method: "POST",
                body: {
                    token: data,
                },
            },
        );
    }

    public update(
        data: Partial<{
            token: RatecardConnection["token"];
            config: RatecardConnection["config"];
        }>,
    ): Promise<ApiResponse<RatecardConnection>> {
        return this.request(
            this.route(
                "api.connections.ratecard.update",
                "/connections/ratecard",
            ),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public delete(): Promise<null> {
        return this.request(
            this.route(
                "api.connections.ratecard.destroy",
                "/connections/ratecard",
            ),
            {
                method: "DELETE",
            },
        );
    }

    public list<T extends Record<string, unknown>>(
        list:
            | "accounts"
            | "campaigns"
            | "contacts"
            | "feedback"
            | "forms"
            | "groups"
            | "groups_contacts"
            | "locales"
            | "messages"
            | "reports"
            | "teams"
            | "users"
            | "webhooks"
            | "webhooks/history",
        data: Partial<
            Record<string, any> & {
                page: number;
                show: number;
                fields: string[];
                except: string[];
                sort: string;
                transform: Record<keyof T, string | false>;
                access_token: string;
                filter: string;
            }
        > = {},
    ): Promise<ApiResponse<T[]>> {
        return this.request<ApiResponse<T[]>>(
            this.route(
                "api.connections.ratecard.list",
                "/connections/ratecard/list/{list}",
                {
                    list,
                },
            ),
            {
                method: "POST",
                body: data,
            },
        );
    }

    public listItem<T extends Record<string, unknown>>(
        list:
            | "accounts"
            | "campaigns"
            | "contacts"
            | "feedback"
            | "forms"
            | "groups"
            | "groups_contacts"
            | "locales"
            | "messages"
            | "reports"
            | "teams"
            | "users"
            | "webhooks"
            | "webhooks/history",
        uuid: string | number,
        data: Partial<
            Record<string, any> & {
                fields: string[];
                except: string[];
                access_token: string;
                transform: Record<keyof T, string | false>;
            }
        > = {},
    ): Promise<ApiResponse<T[]>> {
        return this.request<ApiResponse<T[]>>(
            this.route(
                "api.connections.ratecard.list_item",
                "/connections/ratecard/list/{list}/{uuid}",
                {
                    list,
                    uuid,
                },
            ),
            {
                method: "POST",
                body: data,
            },
        );
    }

    public async listTemplates() {
        const url = useRuntimeConfig().public.ratecardUrl;

        return useFetch<RatecardSurveyTemplate[]>(`${url}/api/surveys/list`);
    }

    public async listTemplatesForAccount(access_token: string) {
        const url = useRuntimeConfig().public.ratecardUrl;

        return useFetch<RatecardSurvey[]>(
            `${url}/api/surveys/list/${access_token}`,
        );
    }

    public async findSurvey(uuid: string, access_token: string) {
        const url = useRuntimeConfig().public.ratecardUrl;

        return useFetch<RatecardSurvey>(
            `${url}/api/surveys/list/${access_token}?value=${uuid}`,
        );
    }

    public async listAccounts(access_token: string) {
        const url = useRuntimeConfig().public.ratecardUrl;

        return useFetch<RatecardAccount[]>(
            `${url}/api/accounts/list/${access_token}`,
        );
    }

    public async useTemplate(slug: string, access_token: string) {
        const url = useRuntimeConfig().public.ratecardUrl;

        return useFetch<RatecardSurvey>(
            `${url}/api/surveys/list/${slug}/use/${access_token}`,
            {
                method: "POST",
            },
        );
    }
}
